import { useState } from 'react'
import type { ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Cell as PieCell, Pie, PieChart, Tooltip, XAxis, YAxis } from 'recharts'
import { AlertTriangle, CalendarClock, DollarSign, Factory, TrendingDown, TrendingUp, Users } from 'lucide-react'
import { api } from '@/lib/api'
import { money } from '@/lib/money'
import { currentMonthInMalaysia, formatDate } from '@/lib/date'
import { cn } from '@/lib/cn'
import { BAR_RADIUS, CHART, gridProps, tooltipProps, xAxisProps, yAxisProps } from '@/lib/chart'
import Card, { SectionLabel } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import PageHeader from '@/components/ui/PageHeader'
import LogoLoader from '@/components/ui/LogoLoader'
import StatTile, { type StatTone } from '@/components/ui/StatTile'
import { ChartBox, ChartLegend } from '@/components/ui/Chart'
import Badge from '@/components/ui/Badge'
import Skeleton from '@/components/ui/Skeleton'
import InsightCard from '@/components/admin/ai/InsightCard'
import { RiskBadge } from '@/components/admin/ai/AiBadges'
import { aiKeys, useBrief, useLateRisks } from '@/lib/aiInsights'
import { aiFeatures } from '@/lib/aiFeatures'

interface Dash {
  customers_total: number
  pieces_today: number
  capacity_today: number | null
  jobs_in_progress: number
  jobs_issue: number
  /** On the floor and past their current section's booked day. */
  jobs_late: number
  routing_pending: number
  orders_period: number
  pieces_period: number
  revenue_period: number
  /** Revenue over the same span one period back (1-3 Sep against 1-3 Oct). */
  revenue_previous: number
  compare_from: string
  compare_to: string
  /** Pieces on orders placed in the period, the same orders revenue counts. */
  pieces_sold: number
  collected: number
  outstanding: number
  chart_pieces: { date: string; label: string; pieces: number }[]
  available_years: number[]
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** One figure in the revenue card's bottom row: a label/value line on phones, stacked from sm. */
function MiniStat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-3 sm:block">
      <p className="truncate text-xs text-foreground/60">{label}</p>
      <p className={cn('truncate text-sm font-medium text-foreground sm:text-base', className)}>{value}</p>
    </div>
  )
}

/**
 * MPG AI's morning brief (written by the scheduler each working morning, or on
 * Refresh) beside the jobs likely to miss their delivery date at the pace the
 * sections really work (Duncan 2026-10-26).
 */
function AiMorning() {
  const brief = useBrief()
  const risks = useLateRisks()
  const list = [...(risks.data?.values() ?? [])]

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <InsightCard
        title="MPG AI morning brief"
        insight={brief.data}
        loading={brief.isLoading}
        queryKey={aiKeys.brief}
        runPath="/admin/ai/brief"
        runLabel="Write today's brief"
        rerunLabel="Refresh"
        emptyText="No brief yet. One is written every working morning at 07:30, or press Write today's brief."
        followUp={aiFeatures.chat ? "About this morning's planning brief: " : undefined}
      />
      <Card className="min-w-0">
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="text-base font-medium text-foreground">Jobs at risk</span>
          <Badge tone={list.length ? 'danger' : 'success'}>{list.length}</Badge>
        </div>
        <p className="mb-3 text-xs text-foreground/60">By how fast each section has really worked in the last 90 days.</p>
        {risks.isLoading && <Skeleton className="h-16" />}
        {!risks.isLoading && list.length === 0 && <p className="text-sm text-foreground/60">Every open job fits its delivery date.</p>}
        <ul className="space-y-2">
          {list.slice(0, 6).map((risk) => (
            <li key={risk.job_id}>
              <Link to={`/admin/orders/${risk.order_id}`} className="block rounded-lg border border-foreground/10 p-2 hover:bg-foreground/5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-bold">{risk.job_code}</span>
                  <RiskBadge risk={risk} />
                </div>
                <p className="mt-1 truncate text-xs text-foreground/70">
                  {risk.customer ?? '-'} - {risk.current_section ?? '-'} - due {formatDate(risk.delivery_due)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
        {list.length > 6 &&
          // The Jobs list only shows the flags once aiFeatures.jobRisk is on.
          (aiFeatures.jobRisk ? (
            <Link to="/admin/jobs" className="mt-2 inline-block text-xs font-medium text-primary hover:underline">
              {list.length - 6} more on Jobs
            </Link>
          ) : (
            <p className="mt-2 text-xs text-foreground/60">and {list.length - 6} more</p>
          ))}
      </Card>
    </div>
  )
}

export default function AdminDashboard() {
  const nowMonth = currentMonthInMalaysia() // 'YYYY-MM', pinned to Malaysia
  const currentYear = Number(nowMonth.slice(0, 4))
  const currentMonthNum = Number(nowMonth.slice(5, 7))

  const [year, setYear] = useState(currentYear)
  const [month, setMonth] = useState<string>(String(currentMonthNum)) // '' = All months

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['admin-dash', year, month],
    queryFn: async () => (await api.get<Dash>('/admin/dashboard', { params: month ? { year, month } : { year } })).data,
    placeholderData: keepPreviousData,
  })

  if (isLoading || !data) return <LogoLoader />

  // Past years offer all 12 months; the current year stops at the current month
  const maxMonth = year === currentYear ? currentMonthNum : 12
  const monthOptions = [
    { value: '', label: 'All' },
    ...MONTHS.slice(0, maxMonth).map((label, i) => ({ value: String(i + 1), label })),
  ]
  const yearOptions = [...data.available_years].reverse().map((y) => ({ value: String(y), label: String(y) }))

  const changeYear = (value: string) => {
    const y = Number(value)
    setYear(y)
    if (y === currentYear && month !== '' && Number(month) > currentMonthNum) {
      setMonth(String(currentMonthNum))
    }
  }

  const periodLabel = month ? `${MONTHS[Number(month) - 1]} ${year}` : String(year)
  // "vs Sep" for a month (December's previous month is last year's), "vs 2025" for a year
  const compareLabel = month ? MONTHS[(Number(month) + 10) % 12] : String(year - 1)
  const revenueChange =
    data.revenue_previous > 0 ? Math.round(((data.revenue_period - data.revenue_previous) / data.revenue_previous) * 100) : null

  const capacityPct =
    data.capacity_today && data.capacity_today > 0
      ? Math.min(100, Math.round((data.pieces_today / data.capacity_today) * 100))
      : 0

  const liveTiles: { label: string; value: number | string; icon: ReactNode; to: string; tone: StatTone }[] = [
    { label: 'Customers', value: data.customers_total, icon: <Users className="size-5" />, to: '/admin/customers', tone: 'warning' },
    { label: 'Jobs in production', value: data.jobs_in_progress, icon: <Factory className="size-5" />, to: '/admin/jobs', tone: 'primary' },
    { label: 'Issues flagged', value: data.jobs_issue, icon: <AlertTriangle className="size-5" />, to: '/admin/issues', tone: 'danger' },
    { label: 'Late jobs', value: data.jobs_late, icon: <CalendarClock className="size-5" />, to: '/admin/jobs?late=1', tone: 'danger' },
  ]

  const donutData = [
    { name: 'In production', value: data.jobs_in_progress },
    { name: `Orders - ${periodLabel}`, value: Math.max(1, data.orders_period) },
  ]

  // Centred, because the tiles stretch to the revenue card's height beside them
  const tile = (t: (typeof liveTiles)[number]) => <StatTile key={t.label} {...t} className="flex items-center" />

  return (
    <div>
      <PageHeader
        title="General Report"
        subtitle="Bird's-eye view across the whole platform"
        actions={
          <div className="flex w-full items-end gap-3 sm:w-auto">
            <Field label="Month" className="flex-1 sm:w-32 sm:flex-none">
              <Select value={month} onChange={setMonth} options={monthOptions} />
            </Field>
            <Field label="Year" className="flex-1 sm:w-28 sm:flex-none">
              <Select value={String(year)} onChange={changeYear} options={yearOptions} />
            </Field>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Revenue is the headline; the order count rides along because both follow the period filter */}
        <Link to="/admin/orders" className={cn('block transition-opacity', isFetching && 'opacity-60')}>
          <Card className="flex h-full flex-col justify-between transition-shadow hover:shadow-md">
            <div className="flex items-center gap-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-full border border-primary/60 bg-primary/20 text-primary">
                <DollarSign className="size-6" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm text-foreground/60">Revenue - {periodLabel}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <p className="whitespace-nowrap text-3xl font-medium text-foreground sm:text-4xl">{money(data.revenue_period)}</p>
                  {revenueChange !== null && (
                    <span
                      title={`Compared with ${formatDate(data.compare_from)} - ${formatDate(data.compare_to)}`}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
                        revenueChange >= 0 ? 'border-success/60 bg-success/20 text-success' : 'border-danger/60 bg-danger/20 text-danger',
                      )}
                    >
                      {revenueChange >= 0 ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
                      {revenueChange >= 0 ? '+' : ''}
                      {revenueChange}% vs {compareLabel}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-foreground/60">
                  from {data.orders_period} {data.orders_period === 1 ? 'order' : 'orders'}
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-2 border-t border-foreground/10 pt-4 sm:grid-cols-3 sm:gap-3">
              <MiniStat label="Collected" value={money(data.collected)} />
              <MiniStat label="Outstanding" value={money(data.outstanding)} className={data.outstanding > 0 ? 'text-danger' : undefined} />
              <MiniStat label="Pieces sold" value={`${data.pieces_sold.toLocaleString('en-MY')} pcs`} />
            </div>
          </Card>
        </Link>

        <div className="grid gap-4 sm:grid-cols-2">{liveTiles.map(tile)}</div>
      </div>

      <div className={isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <SectionLabel>
              {month ? 'Pieces booked per day' : 'Pieces booked per month'} - {periodLabel}
            </SectionLabel>
            <ChartBox>
              <BarChart data={data.chart_pieces}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="label" {...xAxisProps} tick={{ fontSize: 12, fill: CHART.tick }} />
                <YAxis {...yAxisProps} />
                <Tooltip {...tooltipProps} />
                <Bar dataKey="pieces" fill={CHART.primary} radius={BAR_RADIUS} />
              </BarChart>
            </ChartBox>
          </Card>

          <Card>
            <SectionLabel>Workload split</SectionLabel>
            <ChartBox height={160}>
              <PieChart>
                <Pie data={donutData} dataKey="value" innerRadius="70%" outerRadius="95%" paddingAngle={4} cornerRadius={5}>
                  {donutData.map((_, i) => (
                    <PieCell key={i} fill={CHART.donut[i]!.fill} stroke={CHART.donut[i]!.stroke} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ChartBox>
            <ChartLegend
              className="flex-col gap-y-1.5"
              items={donutData.map((d, i) => ({ label: d.name, fill: CHART.donut[i]!.fill, stroke: CHART.donut[i]!.stroke }))}
            />
          </Card>
        </div>
      </div>

      <div className="mt-4">
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <SectionLabel className="mb-1">Today's capacity</SectionLabel>
              <p className="text-2xl font-medium text-foreground">
                {data.pieces_today}
                <span className="text-base text-foreground/60"> / {data.capacity_today ?? 'closed'} pcs</span>
              </p>
            </div>
            <Link to="/admin/capacity" className="text-sm font-medium text-primary hover:underline">
              Manage capacity
            </Link>
          </div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-foreground/10">
            <div
              className={`h-full rounded-full ${capacityPct >= 100 ? 'bg-danger' : 'bg-primary'}`}
              style={{ width: `${capacityPct}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-foreground/60">{capacityPct}% of in-house daily limit used</p>
        </Card>
      </div>

      {/* Below the figures (client 2026-10-26: the top is for the report). */}
      <AiMorning />
    </div>
  )
}
